/**
 * Куда вести человека «на выход» — выбор целей эвакуации по данным площадки.
 *
 * Логика жила во вьюере (web/src/emergency/escape-route.ts) и понадобилась
 * второй раз: b2b при печати таблички зашивает путь к выходу прямо в QR
 * (параметр `&r=`, план 21.09). Кабинет не имеет права импортировать код
 * вьюера, поэтому выбор целей вынесен сюда — один источник правды на оба
 * применения, с тестами.
 *
 * Отдельной категории POI «выход» в таксономии нет: выходы живут в
 * `evacuation.exits`, в `safety[]` с kind `fire_exit` и, как последний рубеж,
 * в самом графе — узлы `kind: 'entrance'`. Вход и есть выход наружу, и на
 * живых картах (Ülemiste, 31.07) без этого запасного варианта не строилась
 * ни одна линия.
 */
import { haversineM, type LonLat } from '@app/geo'
import type { EmergencyMode } from './emergency-graph'
import type { BuiltGraph } from './graph'
import type { Venue } from './types'

export type EscapeTargetKind = 'exit' | 'assembly' | 'shelter'

export interface EscapeTarget {
  readonly id: string
  readonly kind: EscapeTargetKind
  readonly point: LonLat
  /** Узел графа, если цель к нему привязана (у точек сбора его не бывает). */
  readonly node?: string | undefined
}

/** Дальше этого расстояния «привязка к ближайшему узлу» — уже выдумка. */
export const SNAP_RADIUS_M = 40

/** Безопасные точки (ADR 0010) — какие из них считаются целью эвакуации. */
const SAFETY_TARGET_KIND: Readonly<Record<string, EscapeTargetKind>> = {
  fire_exit: 'exit',
  assembly: 'assembly',
  muster: 'assembly',
  shelter: 'shelter',
}

/**
 * Все цели эвакуации площадки нужных видов. Фолбэк на узлы-входы сюда НЕ
 * входит — он отдельной функцией, чтобы вызывающий сам решал, спускаться ли
 * на этот уровень честности.
 */
export function escapeTargets(
  v: Venue,
  kinds: readonly EscapeTargetKind[] = ['exit', 'assembly', 'shelter'],
): readonly EscapeTarget[] {
  const want = new Set(kinds)
  return [...evacuationTargets(v), ...safetyTargets(v)].filter((t) => want.has(t.kind))
}

/** Цели из секции evacuation: выходы, точки сбора, укрытия. */
function evacuationTargets(v: Venue): EscapeTarget[] {
  const evac = v.evacuation
  const asTarget = (kind: EscapeTargetKind) => (i: { id: string; point: LonLat; node?: string }) =>
    ({ id: i.id, kind, point: i.point, node: i.node }) satisfies EscapeTarget
  return [
    ...(evac?.exits ?? []).map(asTarget('exit')),
    ...(evac?.assembly_points ?? []).map(asTarget('assembly')),
    ...(evac?.shelters ?? []).map(asTarget('shelter')),
  ]
}

/** Цели из safety[]: пожарный выход, точка сбора/муштры, укрытие. */
function safetyTargets(v: Venue): EscapeTarget[] {
  return (v.safety ?? []).flatMap((pt) => {
    const kind = SAFETY_TARGET_KIND[pt.kind]
    return kind ? [{ id: pt.id, kind, point: pt.point, node: pt.node }] : []
  })
}

/** Узлы-входы графа как цели последней надежды («выход у здания есть всегда»). */
export function entranceTargets(v: Venue): readonly EscapeTarget[] {
  return v.network.nodes
    .filter((n) => n.kind === 'entrance')
    .map((n) => ({ id: n.id, kind: 'exit' as const, point: n.point, node: n.id }))
}

/**
 * Узлы-цели аварийного режима. Повторяет поведение вьюера дословно:
 * EVACUATION — выходы, а если их нет, входы графа; SHELTER — только укрытия
 * (подменять укрытие выходом нельзя); DRILL — пусто, UI честно говорит
 * «следуйте указаниям персонала».
 */
export function escapeTargetNodeIds(v: Venue, mode: EmergencyMode): readonly string[] {
  const evac = v.evacuation
  if (mode === 'EVACUATION') {
    const ids = nodeIdsOf(evac?.exits)
    return ids.length ? ids : entranceTargets(v).map((t) => t.id)
  }
  if (mode === 'SHELTER') return nodeIdsOf(evac?.shelters)
  return []
}

function nodeIdsOf(items: readonly { readonly node?: string }[] | undefined): string[] {
  return (items ?? []).flatMap((i) => (i.node ? [i.node] : []))
}

/** Ближайший к точке узел графа не дальше radiusM; null — сеть не дотянута. */
export function nearestNodeWithin(
  g: BuiltGraph,
  point: LonLat,
  radiusM = SNAP_RADIUS_M,
): string | null {
  let best: string | null = null
  let bestM = radiusM
  for (const [id, node] of g.nodes) {
    const d = haversineM(node.point, point)
    if (d <= bestM) {
      bestM = d
      best = id
    }
  }
  return best
}

/**
 * Узлы-цели для таблички: любой выход, точка сбора или укрытие, куда доводит
 * сеть. Цели без своего узла (точки сбора их не имеют вовсе) привязываются к
 * ближайшему узлу графа в радиусе snapRadiusM — дальше привязывать нечестно:
 * линия пошла бы не туда, куда человек пойдёт.
 */
export function signTargetNodeIds(
  v: Venue,
  g: BuiltGraph,
  snapRadiusM = SNAP_RADIUS_M,
): readonly string[] {
  const ids = new Set<string>()
  for (const t of escapeTargets(v)) {
    const node = t.node && g.nodes.has(t.node) ? t.node : nearestNodeWithin(g, t.point, snapRadiusM)
    if (node) ids.add(node)
  }
  if (ids.size === 0) {
    for (const t of entranceTargets(v)) if (g.nodes.has(t.id)) ids.add(t.id)
  }
  return [...ids]
}
